import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate, formatDateTime, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  UserPlus, CalendarHeart, CalendarCheck2, AlertTriangle, FolderHeart, AlarmClock,
  FileText, DollarSign, Handshake, Dumbbell, ListChecks, Upload, ArrowRight,
  TrendingUp, Wallet, Split, Clock, PhoneOff, CalendarClock, ChevronRight, Layers,
} from "lucide-react";

const NAVY = "#1C4358", RED = "#9C1E22", SKY = "#9BB9CE", YELLOW = "#DEE029",
  KHAKI = "#C4C89B", CHARCOAL = "#252221", GREEN = "#2F7D5A", AMBER = "#B7791F";

const TONES = {
  navy: { chip: "rgba(28,67,88,0.10)", color: NAVY },
  sky: { chip: "rgba(155,185,206,0.24)", color: "#2C6480" },
  green: { chip: "rgba(47,125,90,0.12)", color: GREEN },
  red: { chip: "rgba(156,30,34,0.10)", color: RED },
  khaki: { chip: "rgba(196,200,155,0.30)", color: "#5A5E2E" },
  amber: { chip: "rgba(183,121,31,0.14)", color: AMBER },
  yellow: { chip: "rgba(222,224,41,0.22)", color: "#7A7C12" },
};

const LANE_TONE = {
  sell_home: RED, senior_placement: NAVY, home_safety: GREEN,
  move_into_my_home: SKY, downsizing: KHAKI, needs_guidance: AMBER,
};

const leadName = (l) =>
  l?.name || [l?.first_name, l?.last_name].filter(Boolean).join(" ") || l?.contact_name || l?.senior_name || "New lead";
const caseTitle = (c) => c?.title || c?.case_name || c?.case_number || "Case";

// ---------------------------------------------------------------------------
const SectionHeader = ({ icon: Icon, title, hint, highlight }) => (
  <div className="flex items-end justify-between gap-3 mb-3">
    <div>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={18} className="text-[#1C4358]" />}
        <h3 className="font-display text-lg font-bold text-[#1C4358]">{title}</h3>
        {highlight && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
            style={{ backgroundColor: "rgba(222,224,41,0.30)", color: "#6E700F" }}>{highlight}</span>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, tone = "navy", onClick, testid }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <Card onClick={onClick}
      className={`p-4 border-[#E6EDF2] transition-shadow ${onClick ? "cursor-pointer hover:shadow-[var(--mmm-shadow-md)]" : ""}`}
      style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
      <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-2.5" style={{ backgroundColor: t.chip }}>
        <Icon size={18} style={{ color: t.color }} />
      </div>
      <div className="text-2xl font-bold tabular-nums font-display text-[#252221] leading-none">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground mt-1.5 leading-tight">{label}</div>
    </Card>
  );
};

const PipelineCard = ({ stage, max, onClick }) => {
  const pct = max > 0 ? Math.round((stage.count / max) * 100) : 0;
  return (
    <button onClick={onClick} data-testid={`pipeline-${stage.key}`}
      className="text-left rounded-xl border border-[#E6EDF2] bg-white p-3.5 transition-shadow hover:shadow-[var(--mmm-shadow-md)]"
      style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xl font-bold tabular-nums font-display text-[#1C4358]">{stage.count}</span>
      </div>
      <div className="text-xs font-medium text-[#252221] mt-1 leading-tight min-h-[2rem]">{stage.label}</div>
      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(155,185,206,0.22)" }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: "var(--mmm-navy)" }} />
      </div>
    </button>
  );
};

const LaneCard = ({ lane, onClick }) => {
  const color = LANE_TONE[lane.key] || NAVY;
  return (
    <button onClick={onClick} data-testid={`lane-${lane.key}`}
      className="text-left rounded-xl border border-[#E6EDF2] bg-white p-4 flex items-center gap-3 transition-shadow hover:shadow-[var(--mmm-shadow-md)]"
      style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
      <span className="h-10 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div className="min-w-0 flex-1">
        <div className="text-2xl font-bold tabular-nums font-display text-[#252221] leading-none">{lane.count}</div>
        <div className="text-xs text-muted-foreground mt-1 leading-tight">{lane.label}</div>
      </div>
    </button>
  );
};

const RevStat = ({ icon: Icon, label, value, sub, tone = "navy" }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={`rev-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.chip }}>
          <Icon size={15} style={{ color: t.color }} />
        </div>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="text-xl font-bold tabular-nums font-display text-[#252221]">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
};

const ListCard = ({ icon: Icon, title, count, tone = "navy", items, empty, render }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={`urgent-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.chip }}>
            <Icon size={16} style={{ color: t.color }} />
          </div>
          <h4 className="font-semibold text-sm text-[#252221]">{title}</h4>
        </div>
        <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full"
          style={{ backgroundColor: count > 0 ? t.chip : "rgba(75,85,99,0.08)", color: count > 0 ? t.color : "#6B7280" }}>{count}</span>
      </div>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{empty}</p>
      ) : (
        <div className="divide-y divide-[#F0F4F7]">{items.map(render)}</div>
      )}
    </Card>
  );
};

// ===========================================================================
export default function Dashboard() {
  const { user, activeBranch } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = activeBranch ? { branch_id: activeBranch } : {};
        const res = await api.get("/dashboard", { params });
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeBranch]);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = user?.full_name?.split(" ")[0] || "there";
  const todayLabel = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const quickActions = [
    { label: "Add New Lead", icon: UserPlus, to: "/leads?new=1", primary: true, testid: "qa-add-lead" },
    { label: "Book Consultation", icon: CalendarHeart, to: "/consultations?new=1", testid: "qa-book-consultation" },
    { label: "Open Family Case", icon: FolderHeart, to: "/cases?new=1", testid: "qa-open-case" },
    { label: "Add Partner", icon: Handshake, to: "/partners?new=1", testid: "qa-add-partner" },
    { label: "Add Task", icon: ListChecks, to: "/tasks?new=1", testid: "qa-add-task" },
    { label: "Add Revenue Opportunity", icon: DollarSign, to: "/revenue?new=1", testid: "qa-add-revenue" },
    { label: "Upload Document", icon: Upload, to: "/documents", testid: "qa-upload-document" },
  ];

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const snap = data.snapshot || {};
  const pipeline = data.pipeline || [];
  const lanes = data.service_lane_breakdown || [];
  const rev = data.revenue_snapshot || {};
  const urgent = data.urgent || {};
  const uc = data.urgent_counts || {};
  const pipelineMax = Math.max(1, ...pipeline.map((p) => p.count));

  const snapItems = [
    { label: "New leads today", value: snap.new_leads_today, icon: UserPlus, tone: "navy", to: "/leads" },
    { label: "Free consults booked", value: snap.consults_booked_today, icon: CalendarHeart, tone: "sky", to: "/consultations" },
    { label: "Consults completed", value: snap.consults_completed_today, icon: CalendarCheck2, tone: "green", to: "/consultations" },
    { label: "Urgent family cases", value: snap.urgent_cases, icon: AlertTriangle, tone: "red", to: "/cases" },
    { label: "Active cases", value: snap.active_cases, icon: FolderHeart, tone: "navy", to: "/cases" },
    { label: "Overdue tasks", value: snap.overdue_tasks, icon: AlarmClock, tone: "red", to: "/tasks" },
    { label: "New documents", value: snap.new_documents_today, icon: FileText, tone: "sky", to: "/documents" },
    { label: "Open revenue opps", value: snap.open_revenue_opps, icon: DollarSign, tone: "green", to: "/revenue" },
    { label: "New partner referrals", value: snap.new_partner_referrals_today, icon: Handshake, tone: "khaki", to: "/partners" },
    { label: "Trilogy outreach", value: snap.trilogy_outreach_today, icon: Dumbbell, tone: "yellow", to: "/trilogy-outreach" },
  ];

  const byLane = Object.entries(rev.by_lane || {}).map(([name, value]) => ({ name: name.replace(/ .*/, ""), full: name, value }));
  const byBranch = Object.entries(rev.by_branch || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-7">
      {/* Greeting */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221]">{greet}, {firstName}</h2>
          <p className="text-sm text-muted-foreground">{todayLabel} · Your senior transition pipeline at a glance.</p>
        </div>
      </div>

      {/* Quick Actions */}
      <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="quick-actions">
        <SectionHeader icon={Layers} title="Quick Actions" />
        <div className="flex flex-wrap gap-2">
          {quickActions.map((a) => (
            <Button key={a.label} variant={a.primary ? "default" : "outline"} onClick={() => navigate(a.to)}
              data-testid={a.testid}
              className={a.primary ? "text-white" : "border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]"}
              style={a.primary ? { backgroundColor: "var(--mmm-red)" } : {}}>
              <a.icon size={16} className="mr-1.5" /> {a.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* 1. Today's Snapshot */}
      <section>
        <SectionHeader icon={TrendingUp} title="Today's Snapshot" hint="Activity and live counts for your branch" highlight="Live" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {snapItems.map((s) => (
            <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} tone={s.tone}
              onClick={() => navigate(s.to)} testid={`snapshot-${s.label.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>
      </section>

      {/* 2. Pipeline Overview */}
      <section>
        <SectionHeader icon={ArrowRight} title="Pipeline Overview" hint="Where families are across the journey" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {pipeline.map((stage) => (
            <PipelineCard key={stage.key} stage={stage} max={pipelineMax} onClick={() => navigate(stage.to || "/cases")} />
          ))}
        </div>
      </section>

      {/* 3. Service Lane Breakdown */}
      <section>
        <SectionHeader icon={FolderHeart} title="Service Lane Breakdown" hint="Active cases by lane of service" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {lanes.map((lane) => (
            <LaneCard key={lane.key} lane={lane} onClick={() => navigate("/cases")} />
          ))}
        </div>
      </section>

      {/* 4. Revenue Snapshot */}
      <section>
        <SectionHeader icon={DollarSign} title="Revenue Snapshot" hint="Pipeline value, earnings and pending payouts" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <RevStat icon={TrendingUp} label="Expected Revenue" value={formatCurrency(rev.expected)} tone="navy" sub="Pipeline + won (excl. lost)" />
          <RevStat icon={DollarSign} label="Actual Revenue" value={formatCurrency(rev.actual)} tone="green" sub={`${formatCurrency(rev.paid)} collected`} />
          <RevStat icon={Wallet} label="Pending Payment" value={formatCurrency(rev.pending_payment)} tone="amber" sub="Won / invoiced, not paid" />
          <RevStat icon={Split} label="Splits Pending" value={formatCurrency(rev.split_pending_amount)} tone="red" sub={`${rev.split_pending_count || 0} payout${(rev.split_pending_count || 0) === 1 ? "" : "s"} owed`} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="revenue-by-lane">
            <h4 className="font-semibold text-sm text-[#252221] mb-3">Revenue by Lane</h4>
            {byLane.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">No earned revenue recorded yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byLane} margin={{ left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#4B5563" }} interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip cursor={{ fill: "rgba(155,185,206,0.12)" }} formatter={(v, n, p) => [formatCurrency(v), p?.payload?.full]} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {byLane.map((_, i) => <Cell key={i} fill={[NAVY, GREEN, SKY, KHAKI, RED, AMBER, CHARCOAL][i % 7]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="revenue-by-branch">
            <h4 className="font-semibold text-sm text-[#252221] mb-3">Revenue by Branch</h4>
            {byBranch.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">No earned revenue recorded yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byBranch} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#4B5563" }} width={110} />
                  <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: "rgba(155,185,206,0.12)" }} />
                  <Bar dataKey="value" fill={RED} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      </section>

      {/* 5. Urgent Follow-Up */}
      <section>
        <SectionHeader icon={AlertTriangle} title="Urgent Follow-Up" hint="Things that need attention now" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ListCard icon={Clock} title="Overdue Tasks" count={uc.overdue_tasks || 0} tone="red"
            items={urgent.overdue_tasks} empty="No overdue tasks. All caught up!"
            render={(t) => (
              <button key={t.id} onClick={() => navigate("/tasks")} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[rgba(155,185,206,0.08)] -mx-2 px-2 rounded-lg">
                <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{t.title}</div>
                  <div className="text-xs text-[#9C1E22]">Due {formatDate(t.due_date)}</div></div>
                <ChevronRight size={15} className="text-muted-foreground shrink-0" />
              </button>
            )} />
          <ListCard icon={CalendarClock} title="Cases · No Activity 7+ Days" count={uc.stale_cases || 0} tone="amber"
            items={urgent.stale_cases} empty="Every active case has recent activity."
            render={(c) => (
              <button key={c.id} onClick={() => navigate("/cases")} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[rgba(155,185,206,0.08)] -mx-2 px-2 rounded-lg">
                <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{caseTitle(c)}</div>
                  <div className="text-xs text-muted-foreground">Last update {formatDate(c.updated_at)}</div></div>
                <StatusBadge value={c.status} />
              </button>
            )} />
          <ListCard icon={PhoneOff} title="Urgent Leads · Not Contacted" count={uc.uncontacted_leads || 0} tone="navy"
            items={urgent.uncontacted_leads} empty="No new leads waiting. Nicely done!"
            render={(l) => (
              <button key={l.id} onClick={() => navigate("/leads")} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[rgba(155,185,206,0.08)] -mx-2 px-2 rounded-lg">
                <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{leadName(l)}</div>
                  <div className="text-xs text-muted-foreground">Added {formatDate(l.created_at)}</div></div>
                {(l.urgency === "high" || l.urgency_level === "high" || l.urgency_level === "immediate")
                  ? <StatusBadge value="high" /> : <ChevronRight size={15} className="text-muted-foreground shrink-0" />}
              </button>
            )} />
          <ListCard icon={CalendarCheck2} title="Consultations · Need Follow-Up" count={uc.unfollowed_consultations || 0} tone="green"
            items={urgent.unfollowed_consultations} empty="No consultations awaiting follow-up."
            render={(c) => (
              <button key={c.id} onClick={() => navigate("/consultations")} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[rgba(155,185,206,0.08)] -mx-2 px-2 rounded-lg">
                <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{c.family_name || "Consultation"}</div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(c.scheduled_at)}</div></div>
                <ChevronRight size={15} className="text-muted-foreground shrink-0" />
              </button>
            )} />
        </div>
      </section>
    </div>
  );
}
